import React, { useEffect, useCallback, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import NumberFormat from "react-number-format";
import QtyInput from "../output/QtyInput";
import { BiArrowBack, BiTrash, BiPlus } from "react-icons/bi";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";

import {
  getQoBuilder,
  getQoDetail,
  insertQo,
  updateQo,
  updateQoStatus,
  cekNoKontrak,
  resetSaveStatus,
  quotationV2Selector,
} from "./quotationV2Slice";

import { getMinKlien, klienSelector }                       from "../../master/klien/klienSlice";
import { getMinOum, OumSelector }                           from "../../master/oum/oumSlice";
import { getMinRental, rentalSelector }                     from "../../master/rental/rentalSlice";
import { CargoSelector, getMinCargo }                       from "../../master/cargo-carrying-unit/cargoSlice";
import { areaRentalSelector, getMinAreaRental }             from "../../master/area-rental/areaRentalSlice";
import { getMinLabour, labourSelector }                     from "../../master/labour/labourSlice";
import { getMinKendaraan, kendaraanSelector }               from "../../master/kendaraan/kendaraanSlice";
import { getMinLimbah, limbahSelector }                     from "../../master/limbah/limbahSlice";
import { getMinWaste, WasteSelector }                       from "../../master/waste-management/wasteSlice";
import { getMinPortService, portServiceSelector }           from "../../master/port-service/portServiceSlice";
import { getMinRoom, roomSelector }                         from "../../master/room/roomSlice";
import { getMinTypeOfClothing, typeOfClothingSelector }     from "../../master/type-of-clothing/typeOfClothingSlice";
import { getMinGeneralServiceUnit, generalServiceUnitSelector } from "../../master/general-service-unit/generalServiceUnitSlice";
import { fuelSelector, getMinFuel }                         from "../../master/fuel/fuelSlice";
import { getMinGeneralServiceItem, generalServiceItemSelector } from "../../master/general-service-item/generalServiceItemSlice";
import { useMutation }                                      from "@tanstack/react-query";
import { fetchPostSilance }                                 from "../../../fetchers";

import { Colors } from "../../../assets/Colors";

// ── Constants ──────────────────────────────────────────────────────

const ACTIVITY_ORDER = [
  "PORT_SERVICES", "RENTAL", "WASTE_MANAGEMENT_SERVICES",
  "AREA_RENTAL", "MANPOWER", "GENERAL_SERVICE", "HANDLING_FEE",
];

const ACTIVITY_LABELS = {
  PORT_SERVICES:            "Port Services",
  RENTAL:                   "Rental",
  WASTE_MANAGEMENT_SERVICES:"Waste Management Services",
  AREA_RENTAL:              "Area Rental",
  MANPOWER:                 "Manpower",
  GENERAL_SERVICE:          "General Service",
  HANDLING_FEE:             "Handling Fee",
};

// Item aktivitas Handling Fee (tetap): BBM solar, BBM pertalite, listrik, konsumable.
// Nilai "SOLAR" dipertahankan (QO lama menyimpannya); hanya labelnya "BBM Solar".
const HANDLING_FEE_ITEMS = [
  { value: "SOLAR",      label: "BBM Solar" },
  { value: "PERTALITE",  label: "BBM Pertalite" },
  { value: "LISTRIK",    label: "Listrik" },
  { value: "KONSUMABLE", label: "Konsumable" },
];

// Activity yang dikenai Tagihan Fuel Surcharge (FSC) di MJS — dicocokkan ke nama item Port Service.
const FSC_ACTIVITIES = [
  { value: "STEVEDORING", label: "Stevedoring" },
  { value: "CARGODORING", label: "Cargodoring" },
  { value: "RECEIVING",   label: "Receiving" },
  { value: "DELIVERY",    label: "Delivery" },
];

const GENERAL_SUBTYPES = [
  { key: "ROOM",    label: "Accomodation / Room",      serviceType: "F" },
  { key: "LAUNDRY", label: "Laundry",                  serviceType: "F" },
  { key: "MEALS",   label: "Meals",                    serviceType: "F" },
  { key: "WOODEN",  label: "Wooden Dunnage & Pallet",  serviceType: "V" },
  { key: "DRILL",   label: "Drill & Fresh Water",      serviceType: "V" },
  { key: "OTHERS",  label: "Others Service",           serviceType: "V" },
  { key: "VEHICLE", label: "Vehicle Rental",           serviceType: "V" },
];

// ── Small reusable pieces ──────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = {
    draft:     { bg: "bg-gray-100",   text: "text-gray-700",  label: "Draft" },
    ready:     { bg: "bg-green-100",  text: "text-green-700", label: "Siap JR" },
    expired:   { bg: "bg-gray-200",   text: "text-gray-500",  label: "Kadaluarsa" },
    submitted: { bg: "bg-yellow-100", text: "text-yellow-700",label: "Menunggu Approval" },
    approved:  { bg: "bg-green-100",  text: "text-green-700", label: "Disetujui" },
    rejected:  { bg: "bg-red-100",    text: "text-red-700",   label: "Ditolak" },
  }[status] ?? { bg: "bg-gray-100", text: "text-gray-500", label: status };
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

function ServiceTypeBadge({ type }) {
  if (type === "F")
    return <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">TETAP</span>;
  return <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-500">VAR</span>;
}

function SectionHeader({ title }) {
  return (
    <div style={{ background: Colors.primary }}
      className="w-full px-4 py-2 rounded-t text-white font-semibold text-sm">
      {title}
    </div>
  );
}

function AddButton({ onClick, label = "Tambah" }) {
  return (
    <button type="button" onClick={onClick}
      className="flex items-center gap-1 my-2 px-3 py-1.5 bg-white border border-gray-300 rounded shadow-sm text-sm hover:bg-gray-50">
      <BiPlus className="text-base" style={{ color: Colors.primary }} />
      <span style={{ color: Colors.primary }}>{label}</span>
    </button>
  );
}

function DeleteBtn({ onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="p-1 rounded border border-red-300 hover:bg-red-50 text-red-500">
      <BiTrash className="text-base" />
    </button>
  );
}

function TH({ children, className = "" }) {
  return (
    <th style={{ color: Colors.primary }}
      className={`px-2 py-2 text-left text-sm font-semibold border-b border-gray-200 whitespace-nowrap ${className}`}>
      {children}
    </th>
  );
}

function TD({ children }) {
  return <td className="px-2 py-1 border-b border-gray-100 align-middle">{children}</td>;
}

// ── Parameter margin (Handling Fee / FSC): rentang nilai -> persen ──
function FeeParamBlock({ title, rows, onChange, disabled = false }) {
  const list = rows ?? [];
  const setRow = (i, patch) => onChange(list.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="border rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold" style={{ color: Colors.primary }}>{title}</h4>
        <AddButton label="Tambah" onClick={() => onChange([...list, { FeeStart: 0, FeeEnd: 0, Percent: 0 }])} />
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-gray-400">Belum ada parameter.</p>
      ) : (
        <table className="table-auto w-full text-sm">
          <thead>
            <tr><TH className="w-8" /><TH>Fee Start</TH><TH>Fee End</TH><TH>%</TH></tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={i}>
                <TD><DeleteBtn onClick={() => onChange(list.filter((_, idx) => idx !== i))} /></TD>
                <TD>
                  <NumberFormat thousandSeparator className="w-40 border rounded px-2 py-1 text-right"
                    value={r.FeeStart ?? 0}
                    onValueChange={({ value }) => setRow(i, { FeeStart: parseFloat(value) || 0 })} />
                </TD>
                <TD>
                  <NumberFormat thousandSeparator className="w-40 border rounded px-2 py-1 text-right"
                    value={r.FeeEnd ?? 0}
                    onValueChange={({ value }) => setRow(i, { FeeEnd: parseFloat(value) || 0 })} />
                </TD>
                <TD>
                  <NumberFormat decimalScale={4} allowNegative={false} className="w-28 border rounded px-2 py-1 text-right"
                    value={r.Percent ?? 0}
                    onValueChange={({ value }) => setRow(i, { Percent: parseFloat(value) || 0 })} />
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Validasi satu daftar parameter: Fee End >= Fee Start, % >= 0, rentang tidak tumpang tindih.
function cekFeeParams(rows, label) {
  const aktif = (rows ?? []).filter(r => (Number(r.FeeStart) || 0) || (Number(r.FeeEnd) || 0) || (Number(r.Percent) || 0));
  for (let i = 0; i < aktif.length; i++) {
    const r = aktif[i];
    if ((Number(r.FeeEnd) || 0) < (Number(r.FeeStart) || 0)) return `${label}: Fee End harus >= Fee Start (baris ${i + 1})`;
    if ((Number(r.Percent) || 0) < 0) return `${label}: persen tidak boleh negatif`;
  }
  const urut = [...aktif].sort((a, b) => (Number(a.FeeStart) || 0) - (Number(b.FeeStart) || 0));
  for (let i = 1; i < urut.length; i++) {
    if ((Number(urut[i].FeeStart) || 0) <= (Number(urut[i - 1].FeeEnd) || 0))
      return `${label}: rentang ${urut[i - 1].FeeStart}–${urut[i - 1].FeeEnd} tumpang tindih dengan ${urut[i].FeeStart}–${urut[i].FeeEnd}`;
  }
  return "";
}

// ── Line table (generik) ────────────────────────────────────────────

// Nilai terpilih untuk dropdown UoM.
//
// Daftar UoM di-dedupe PER NAMA, karena master menyimpan nama yang sama sekali
// untuk tiap kategori — "Kg" ada sebagai OU0002, OU0015, dan OU0064. Dedupe itu
// menyisakan satu ID saja, sehingga baris yang memakai ID lain tidak menemukan
// pasangannya dan kolomnya tampil KOSONG. Terhitung 328 baris QoLine aktif
// terkena, dan ini juga terjadi saat mengedit quotation biasa.
//
// Perbaikannya: kalau ID baris tak ada di daftar, tampilkan opsi bayangan memakai
// nama dari master lengkap. Nilai aslinya TIDAK diubah — mengganti diam-diam ke ID
// hasil dedupe berarti menulis ulang satuan kontrak tanpa sepengetahuan siapa pun.
function nilaiUom(uomOptions, uomLabelById, id) {
  if (!id) return [];
  const cocok = uomOptions.filter(o => o.value === id);
  if (cocok.length) return cocok;
  return [{ value: id, label: uomLabelById?.[id] ?? id }];
}

function LineTable({ headers, rows, onDelete, renderCells, uomOptions, uomLabelById, satuanKey = "SatuanID", onChange, feeMode = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="table-auto w-full text-sm">
        <thead>
          <tr>
            <TH className="w-8" />
            {headers.map((h, i) => <TH key={i}>{h}</TH>)}
            <TH>UoM</TH>
            <TH>Min Usage</TH>
            {feeMode ? <TH>Persentase (%)</TH> : <TH>Rate (Rp)</TH>}
            {feeMode && <TH>PPN</TH>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <TD><DeleteBtn onClick={() => onDelete(i)} /></TD>
              {renderCells(row, i)}
              <TD>
                <div className="w-32">
                  <Select menuPortalTarget={document.body}
                    value={nilaiUom(uomOptions, uomLabelById, row[satuanKey])}
                    onChange={e => onChange(i, { [satuanKey]: e.value })}
                    options={uomOptions} />
                </div>
              </TD>
              <TD>
                <QtyInput className="w-28 border rounded px-2 py-1 text-right"
                  value={row.MinUsage ?? 0}
                  onChange={v => onChange(i, { MinUsage: parseFloat(v) || 0 })} />
              </TD>
              {feeMode ? (
                <>
                  <TD>
                    <NumberFormat allowNegative={false} decimalScale={4} className="w-28 border rounded px-2 py-1 text-right"
                      value={row.Persentase ?? 0}
                      onValueChange={({ value }) => onChange(i, { Persentase: parseFloat(value) || 0 })} />
                  </TD>
                  <TD>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="w-4 h-4" checked={!!row.Ppn}
                        onChange={e => onChange(i, { Ppn: e.target.checked })} />
                      <span>Kena PPN</span>
                    </label>
                  </TD>
                </>
              ) : (
                <TD>
                  <NumberFormat thousandSeparator className="w-36 border rounded px-2 py-1"
                    value={row.Rate ?? 0}
                    onValueChange={({ value }) => onChange(i, { Rate: parseFloat(value) || 0 })} />
                </TD>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Nomor kontrak diketik bebas — tak ada penomoran otomatis dan tak ada unique
// constraint di database. Aturan di bawah menahan isian asal yang sudah terlanjur
// ada di data lama (ada QO ber-NoKontrak "1"), tanpa memaksakan satu format:
// nomor kontrak klien datang dalam macam-macam bentuk, dari "4710005687" sampai
// "14.417/RKSHO-DIR/XI/2025".
function validasiNoKontrak(nilai) {
  const v = (nilai ?? "").trim();
  if (!v)             return "Wajib diisi";
  if (v.length < 5)   return "Terlalu pendek — tulis nomor kontrak selengkapnya (min. 5 karakter)";
  if (v.length > 100) return "Maksimal 100 karakter";
  if (!/\d/.test(v)) return "Harus memuat angka — nomor kontrak tidak pernah berupa huruf saja";
  return null;
}

// ── Main Page Component ────────────────────────────────────────────

export default function QuotationFormPage() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const editQoNo   = location.state?.QoNo ?? null; // passed from monitor page

  // Mode amandemen: dibuka lewat tombol "Amandemen" pada BARIS quotation induk di
  // daftar, jadi induknya datang dari baris yang diklik — bukan diketik pengguna.
  // Itu yang membuat amandemen yatim mustahil terbentuk lewat jalur ini.
  const induk = location.state?.ParentQoNo
    ? {
        QoNo:       location.state.ParentQoNo,
        KlienID:    location.state.ParentKlienID    ?? "",
        NamaKlien:  location.state.ParentNamaKlien  ?? "",
        NoKontrak:  location.state.ParentNoKontrak  ?? "",
        StartDate:  location.state.ParentStartDate  ?? "",
        ExpireDate: location.state.ParentExpireDate ?? "",
      }
    : null;

  const qoV2         = useSelector(quotationV2Selector);
  const klien        = useSelector(klienSelector);
  const oum          = useSelector(OumSelector);
  const rental       = useSelector(rentalSelector);
  const cargo        = useSelector(CargoSelector);
  const areaRental   = useSelector(areaRentalSelector);
  const labour       = useSelector(labourSelector);
  const kendaraan    = useSelector(kendaraanSelector);
  const limbah       = useSelector(limbahSelector);
  const waste        = useSelector(WasteSelector);
  const portService  = useSelector(portServiceSelector);
  const room         = useSelector(roomSelector);
  const clothing     = useSelector(typeOfClothingSelector);
  const generalUnit  = useSelector(generalServiceUnitSelector);
  const fuel         = useSelector(fuelSelector);
  // Item Wooden / Drill / Others — dulu konstanta di constants/activityOptions.js.
  const gsItem       = useSelector(generalServiceItemSelector);

  const mealsMutation = useMutation(() => fetchPostSilance("Master/Meals/getAll"));
  const mealOptions   = useMemo(() => {
    if (!mealsMutation?.data?.data) return [];
    return mealsMutation.data.data.map(m => ({ value: m.MealsName, label: m.MealsName }));
  }, [mealsMutation.data]);

  // ── Form state ──
  const [form, setForm]           = useState(null); // mirrors QoHeaderDetail
  const [errors, setErrors]       = useState({});
  const [isSaving, setIsSaving]   = useState(false);
  const [isKirim,  setIsKirim]    = useState(false);
  // "Kirim untuk Approval" = simpan dulu, baru ubah status. Efek navigasi pada
  // isSaveSuccess di bawah harus dilewati selama rangkaian itu — kalau tidak,
  // halaman berpindah begitu simpan sukses dan panggilan ubah-status terjadi
  // setelah komponen di-unmount, sehingga QO tersimpan tapi TIDAK terkirim.
  const kirimSetelahSimpan = useRef(false);

  // ── Load master data ──
  useEffect(() => {
    dispatch(getMinKlien({ search: "" }));
    dispatch(getMinOum({ search: "" }));
    dispatch(getMinRental({ search: "" }));
    dispatch(getMinCargo({ search: "" }));
    dispatch(getMinAreaRental({ search: "" }));
    dispatch(getMinLabour({ search: "" }));
    dispatch(getMinKendaraan({ search: "" }));
    dispatch(getMinLimbah({ search: "" }));
    dispatch(getMinWaste({ search: "" }));
    dispatch(getMinPortService());
    dispatch(getMinRoom({ search: "" }));
    dispatch(getMinTypeOfClothing({ search: "" }));
    dispatch(getMinGeneralServiceUnit({ search: "" }));
    dispatch(getMinFuel({ search: "" }));
    dispatch(getMinGeneralServiceItem());
    mealsMutation.mutate();

    if (editQoNo) {
      dispatch(getQoDetail(editQoNo));
    } else {
      dispatch(getQoBuilder());
      // Mode amandemen: isi kontrak induk ikut ditarik supaya aktivitas dan
      // barisnya bisa disalin — pengguna tak perlu mengetik ulang seluruh
      // kontrak hanya untuk mengubah beberapa baris.
      if (induk) dispatch(getQoDetail(induk.QoNo));
    }
  }, []); // eslint-disable-line

  // ── Init form setelah builder/detail loaded ──
  useEffect(() => {
    if (editQoNo && qoV2.isDetailSuccess && qoV2.detail && !form) {
      setForm(JSON.parse(JSON.stringify(qoV2.detail)));
    } else if (!editQoNo && induk && !form) {
      // Amandemen: TUNGGU detail induk selesai dimuat. Tanpa penantian ini form
      // sempat terbentuk dari builder kosong, lalu isi induknya tak pernah masuk
      // karena syarat `!form` sudah tak terpenuhi.
      if (!qoV2.isBuilderSuccess || !qoV2.builder) return;
      if (!qoV2.isDetailSuccess  || !qoV2.detail)  return;

      const base   = JSON.parse(JSON.stringify(qoV2.builder));
      const parent = qoV2.detail;

      base.QoType     = "2";
      base.ParentQoNo = induk.QoNo;
      // Klien dan nomor kontrak TIDAK boleh berbeda dari induknya — amandemen
      // atas kontrak lain bukan amandemen. Keduanya dikunci di tampilan.
      base.KlienID    = parent.KlienID   ?? induk.KlienID;
      base.NoKontrak  = parent.NoKontrak ?? induk.NoKontrak;
      base.WorkDesc   = "";
      base.StartDate  = parent.StartDate  || base.StartDate;
      base.ExpireDate = parent.ExpireDate || base.ExpireDate;

      // Aktivitas: keenamnya selalu ada di setiap QO, yang berbeda hanya penanda
      // aktifnya. Jadi yang disalin adalah IsActive-nya, bukan daftarnya.
      const aktifInduk = new Set(
        (parent.Activities ?? []).filter(a => a.IsActive).map(a => a.ActivityName),
      );
      base.Activities = (base.Activities ?? []).map(a => ({
        ...a,
        IsActive: aktifInduk.has(a.ActivityName),
      }));

      // Baris: disalin sebagai baris BARU (LineID 0) yang masing-masing membawa
      // SupersedesLineID ke baris induk asalnya. Itu yang membuat penyalinan aman
      // — baris induk yang ditunjuk gugur saat perakitan template JR, sehingga
      // satu layanan tak pernah muncul dua kali.
      base.HandlingFeeParams = (parent.HandlingFeeParams ?? []).map(r => ({ ...r }));
      base.FscParams = (parent.FscParams ?? []).map(r => ({ ...r }));
      base.FscActivities = [...(parent.FscActivities ?? [])];
      base.Lines = (parent.Lines ?? []).map(l => ({
        ...l,
        LineID: 0,
        SupersedesLineID: l.LineID,
      }));

      setForm(base);
    } else if (!editQoNo && qoV2.isBuilderSuccess && qoV2.builder && !form) {
      const base = JSON.parse(JSON.stringify(qoV2.builder));
      // Default expire date = 1 tahun dari sekarang
      const expire = new Date();
      expire.setFullYear(expire.getFullYear() + 1);
      base.StartDate  = new Date().toISOString().slice(0, 10);
      base.ExpireDate = expire.toISOString().slice(0, 10);
      base.QoType     = "1";
      setForm(base);
    }
  }, [qoV2.isBuilderSuccess, qoV2.builder, qoV2.isDetailSuccess, qoV2.detail]);

  // ── Navigate on save success ──
  useEffect(() => {
    if (qoV2.isSaveSuccess) {
      if (kirimSetelahSimpan.current) {
        // handleKirim yang akan memberi toast & navigasi setelah status berubah.
        dispatch(resetSaveStatus());
        return;
      }
      toast.success("QO berhasil disimpan!");
      dispatch(resetSaveStatus());
      navigate("/quotation-or-contract-list");
    }
  }, [qoV2.isSaveSuccess]);

  // ── Derived options ──
  const rentalSelect = useMemo(() => {
    const r = (rental.minData ?? []).map(x => ({ ...x, CategoryID: x.CategoryID }));
    const c = (cargo.minData ?? []).map(x => ({ ...x, CategoryID: "3" }));
    return [...r, ...c];
  }, [rental.minData, cargo.minData]);

  // Opsi Port Services kini dari master (dulu konstanta `port_service`).
  const portServiceOptions = useMemo(
    () => portService.minData ?? [],
    [portService.minData]
  );

  // Nilai tersimpan yang TIDAK ada di daftar aktif tetap harus tampil.
  //
  // Dulu barisnya `port_service.filter(o => o.value === row.ItemName)`: begitu
  // sebuah kode tak ada di daftar, Select menerima array kosong dan kolom Layanan
  // tampil KOSONG — lalu ikut hilang kalau QO disimpan ulang. Itu bukan hipotesis:
  // 8 baris di 5 QO approved memakai "Vessel_berthing" (b kecil) yang memang tak
  // pernah ada di daftar hardcode. Kode yang sudah dipensiunkan di master juga
  // akan bernasib sama.
  //
  // Karena itu, kalau kodenya tak ditemukan, opsinya dirakit dari ItemDisplay yang
  // memang sudah tersimpan di baris itu.
  const portServiceValue = useCallback(
    (row) => {
      if (!row?.ItemName) return null;
      const found = portServiceOptions.find((o) => o.value === row.ItemName);
      return found ?? { value: row.ItemName, label: row.ItemDisplay || row.ItemName };
    },
    [portServiceOptions]
  );

  // Hilangkan UoM dengan nama yang sama (master menyimpannya per kategori,
  // sehingga tanpa dedupe satu nama UoM muncul berkali-kali di dropdown).
  const uomAll = useMemo(() => {
    const seen = new Set();
    return (oum.minData ?? []).filter(o => {
      const key = (o.label ?? "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [oum.minData]);

  // Peta ID → nama dari master LENGKAP (sebelum dedupe), untuk menampilkan satuan
  // baris yang ID-nya tersingkir dari dropdown. Lihat catatan di nilaiUom().
  const uomLabelById = useMemo(() => {
    const m = {};
    (oum.minData ?? []).forEach(o => { if (o?.value) m[o.value] = o.label; });
    return m;
  }, [oum.minData]);

  // ── Helpers ──
  const updateHeader = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }, []);

  const toggleActivity = useCallback((activityName) => {
    setForm(prev => {
      const updated = { ...prev };
      updated.Activities = (prev.Activities ?? []).map(a =>
        a.ActivityName === activityName ? { ...a, IsActive: !a.IsActive } : a
      );
      return updated;
    });
  }, []);

  const isActivityActive = useCallback((name) => {
    if (!form) return false;
    return form.Activities?.find(a => a.ActivityName === name)?.IsActive ?? false;
  }, [form]);

  // Lines helpers
  const linesOf = useCallback((activityName, lineGroup = "SERVICE") => {
    if (!form?.Lines) return [];
    if (lineGroup === "SERVICE")
      return form.Lines.filter(l => l.LineGroup === "SERVICE" && l.ActivityName === activityName);
    return form.Lines.filter(l => l.LineGroup === lineGroup);
  }, [form]);

  const addLine = useCallback((activityName, lineGroup, defaults = {}) => {
    setForm(prev => {
      const newLine = {
        LineID: 0, ActivityName: activityName, LineGroup: lineGroup,
        SubType: "", ServiceType: "V", ItemRefID: "", ItemRefType: "FREE",
        ItemName: "", ItemDisplay: "", CategoryID: "", SatuanID: "",
        MinUsage: 0, Rate: 0, Persentase: 0, Ppn: false, SortOrder: 0, IsActive: true,
        ...defaults,
      };
      return { ...prev, Lines: [...(prev.Lines ?? []), newLine] };
    });
  }, []);

  const updateLine = useCallback((activityName, lineGroup, idx, updates) => {
    setForm(prev => {
      const lines = [...(prev.Lines ?? [])];
      // find the i-th line matching this activityName + lineGroup
      let count = 0;
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        const matchService = lineGroup === "SERVICE" && l.LineGroup === "SERVICE" && l.ActivityName === activityName;
        const matchSupport = lineGroup !== "SERVICE" && l.LineGroup === lineGroup;
        if (matchService || matchSupport) {
          if (count === idx) {
            lines[i] = { ...lines[i], ...updates };
            break;
          }
          count++;
        }
      }
      return { ...prev, Lines: lines };
    });
  }, []);

  const deleteLine = useCallback((activityName, lineGroup, idx) => {
    setForm(prev => {
      let count = 0;
      const lines = (prev.Lines ?? []).filter(l => {
        const matchService = lineGroup === "SERVICE" && l.LineGroup === "SERVICE" && l.ActivityName === activityName;
        const matchSupport = lineGroup !== "SERVICE" && l.LineGroup === lineGroup;
        if (matchService || matchSupport) {
          if (count === idx) { count++; return false; }
          count++;
        }
        return true;
      });
      return { ...prev, Lines: lines };
    });
  }, []);

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!form.KlienID)    e.KlienID    = "Wajib diisi";
    const errNoKontrak = validasiNoKontrak(form.NoKontrak);
    if (errNoKontrak)     e.NoKontrak  = errNoKontrak;
    if (!form.WorkDesc)   e.WorkDesc   = "Wajib diisi";
    if (!form.StartDate)  e.StartDate  = "Wajib diisi";
    if (!form.ExpireDate) e.ExpireDate = "Wajib diisi";
    if (!form.Activities?.some(a => a.IsActive)) e.Activities = "Pilih minimal satu aktivitas";
    const errFee = cekFeeParams(form.HandlingFeeParams, "Parameter Handling Fee") || cekFeeParams(form.FscParams, "Parameter FSC");
    if (errFee) {
      e.FeeParams = errFee;
      toast.error(errFee);
    }
    // Accomodation/Room: Capacity Person = maks. penghuni klien per room (dipakai menu Akomodasi).
    const roomTanpaKapasitas = (form.Lines ?? []).some(l =>
      l.ActivityName === "GENERAL_SERVICE" && l.SubType === "ROOM" && !(Number(l.CapacityPerson) >= 1));
    if (roomTanpaKapasitas) {
      e.Capacity = "Capacity Person wajib diisi (minimal 1)";
      toast.error("Accomodation/Room: Capacity Person wajib diisi (minimal 1 orang)");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Pintu keluar dari mode amandemen. Mode itu ditentukan oleh state navigasi,
  // BUKAN oleh URL — /add-quotation-or-contract sama persis untuk keduanya — dan
  // state itu ikut tersimpan di history, jadi menekan F5 pun tetap membawa mode
  // amandemen. Sengaja memakai navigasi keras (bukan navigate()): halaman ini
  // sudah terlanjur memegang salinan isi kontrak induk di state-nya, dan hanya
  // pemuatan ulang yang benar-benar memberi form kosong.
  const keluarModeAmandemen = () => {
    const ok = window.confirm(
      "Keluar dari mode amandemen dan mulai QO baru yang kosong?\n\n" +
      "Isian di halaman ini tidak disimpan."
    );
    if (ok) window.location.assign("/add-quotation-or-contract");
  };

  // Satu tempat perakitan payload untuk kedua tombol simpan, supaya perlakuan
  // yang tak kasat mata — spasi di ujung nomor kontrak yang dibuang — tak bisa
  // ada di satu jalur dan absen di jalur lain.
  const buildPayload = () => ({
    ...form,
    NoKontrak: (form.NoKontrak ?? "").trim(),
    HandlingFeeParams: (form.HandlingFeeParams ?? []).map(r => ({ FeeStart: Number(r.FeeStart) || 0, FeeEnd: Number(r.FeeEnd) || 0, Percent: Number(r.Percent) || 0 })),
    FscParams: (form.FscParams ?? []).map(r => ({ FeeStart: Number(r.FeeStart) || 0, FeeEnd: Number(r.FeeEnd) || 0, Percent: Number(r.Percent) || 0 })),
    FscActivities: form.FscActivities ?? [],
    // Filter hanya lines yang punya ItemName/ItemRefID
    // Area Rental selalu Layanan Tetap — baris warisan QO lama (amandemen) yang
    // masih "V" ikut dikoreksi di sini agar otomatis tercentang di form JR.
    Lines: (form.Lines ?? [])
      .filter(l => l.ItemName?.trim() || l.ItemRefID)
      .map(l => (l.ActivityName === "AREA_RENTAL" && l.LineGroup === "SERVICE")
        ? { ...l, ServiceType: "F" } : l)
      // CapacityPerson dikirim sebagai angka (state input bisa string/kosong).
      .map(l => ({
        ...l,
        CapacityPerson: l.SubType === "ROOM" ? (parseInt(l.CapacityPerson, 10) || 0) : 0,
        Persentase: l.ActivityName === "HANDLING_FEE" ? (Number(l.Persentase) || 0) : 0,
        Ppn: l.ActivityName === "HANDLING_FEE" ? !!l.Ppn : false,
      })),
  });

  // Peringatan nomor kontrak kembar. PERINGATAN, bukan penjaga: perpanjangan
  // kontrak memang lazim memakai nomor yang sama, jadi keputusan lanjut/batal
  // ada di pengguna. Mengembalikan true kalau penyimpanan boleh diteruskan.
  const lolosCekDuplikat = async () => {
    const noKontrak = (form.NoKontrak ?? "").trim();
    // Amandemen yang MEMPERTAHANKAN nomor induknya bukan kembar — itu justru
    // bentuk normalnya. Yang diperiksa hanya kalau nomornya sengaja diubah.
    if (induk && noKontrak.toLowerCase() === (induk.NoKontrak ?? "").trim().toLowerCase()) {
      return true;
    }
    try {
      const res    = await dispatch(cekNoKontrak({ NoKontrak: noKontrak, QoNo: editQoNo ?? "" })).unwrap();
      const kembar = res?.data ?? [];
      if (kembar.length === 0) return true;

      const daftar = kembar
        .map(q => `• ${q.QoNo} — ${q.NamaKlien || q.KlienID} (${q.Status}, ${q.StartDate} s/d ${q.ExpireDate})`)
        .join("\n");
      return window.confirm(
        `Nomor kontrak "${noKontrak}" sudah dipakai ${kembar.length} quotation lain:\n\n${daftar}\n\n` +
        `Perpanjangan kontrak memang boleh memakai nomor yang sama. Kalau ini bukan ` +
        `perpanjangan, batalkan dan periksa lagi nomornya.\n\nTetap lanjut simpan?`
      );
    } catch {
      // Cek kembar adalah bantuan, bukan syarat sah. Kalau panggilannya gagal,
      // penyimpanan tetap jalan — menahannya justru membuat form tak bisa dipakai
      // hanya karena satu endpoint pembantu sedang bermasalah.
      return true;
    }
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Lengkapi data yang wajib diisi");
      return;
    }
    if (!(await lolosCekDuplikat())) return;
    setIsSaving(true);
    const payload = buildPayload();
    // Gagal simpan dulu tak memberi kabar apa pun; kini pesan backend ditampilkan
    // (mis. penolakan edit QO approved yang mempersempit masa berlaku).
    const gagal = (e) => toast.error(e?.message ?? (typeof e === "string" ? e : "Gagal menyimpan QO"));
    if (editQoNo) {
      dispatch(updateQo(payload)).unwrap().catch(gagal).finally(() => setIsSaving(false));
    } else {
      dispatch(insertQo(payload)).unwrap().catch(gagal).finally(() => setIsSaving(false));
    }
  };

  // ── Kirim untuk Approval ──
  // Menyimpan lebih dulu, SELALU — termasuk untuk QO yang sudah ada. Tanpa itu,
  // perubahan yang belum ditekan "Simpan" tidak ikut terkirim dan approver menilai
  // versi yang salah. QO baru pun bisa langsung dikirim: nomornya baru lahir saat
  // insert, jadi diambil dari respons simpan.
  const handleKirimApproval = async () => {
    if (!validate()) {
      toast.error("Lengkapi data yang wajib diisi");
      return;
    }
    // Cek kembar didahulukan: kalau nomornya ternyata salah, pengguna berhenti
    // sebelum ditanyai soal pengiriman yang tak jadi ia lakukan.
    if (!(await lolosCekDuplikat())) return;
    // Konfirmasi menyebut DUA efek yang tak terlihat dari nama tombolnya:
    // penyimpanan ikut terjadi, dan QO terkunci dari pengeditan sesudahnya.
    const ok = window.confirm(
      `Kirim ${editQoNo || "quotation ini"} untuk approval?\n\n` +
      `Perubahan yang belum disimpan akan ikut tersimpan. Setelah terkirim, QO ` +
      `tidak bisa diedit lagi sampai approver menyetujui atau menolaknya.`
    );
    if (!ok) return;
    kirimSetelahSimpan.current = true;
    setIsKirim(true);
    try {
      const payload = buildPayload();
      const res = editQoNo
        ? await dispatch(updateQo(payload)).unwrap()
        : await dispatch(insertQo(payload)).unwrap();

      const qoNo = res?.data?.QoNo || editQoNo;
      if (!qoNo) throw new Error("Nomor QO tidak terbaca setelah simpan");

      await dispatch(updateQoStatus({ QoNo: qoNo, Status: "submitted" })).unwrap();
      toast.success("QO tersimpan dan dikirim untuk approval");
      navigate("/quotation-or-contract-list");
    } catch (e) {
      // Simpan bisa BERHASIL lalu ubah-status gagal; QO tetap tersimpan sebagai
      // draft. Sebut itu supaya user tidak menyangka datanya hilang.
      toast.error(
        (e?.message ?? e ?? "Gagal mengirim QO untuk approval") +
        " — QO tetap tersimpan sebagai draft."
      );
    } finally {
      kirimSetelahSimpan.current = false;
      setIsKirim(false);
      dispatch(resetSaveStatus());
    }
  };

  if (!form) return (
    <div className="flex items-center justify-center h-48 text-gray-400">Memuat data…</div>
  );

  // QO baru / draft / ready semuanya editable (tanpa approval). QO approved hanya
  // untuk bagian FM-IT/BD (CanEditApproved dari backend, ditegakkan di UpdateQo).
  const isApprovedEdit = form.Status === "approved" && form.CanEditApproved === true;
  const isEditable = !form.Status || form.Status === "draft" || form.Status === "ready" || isApprovedEdit;

  // Hanya draft (dan QO baru yang belum punya status) yang boleh dikirim.
  // 'ready' sengaja DIKECUALIKAN: peta transisi backend hanya mengizinkan
  // draft→submitted, jadi menampilkan tombolnya untuk 'ready' berarti menjanjikan
  // aksi yang pasti ditolak.
  const bisaKirim = !form.Status || form.Status === "draft";

  // ── Render ──
  return (
    <div className="md:ml-64 px-4 pb-20">

      {/* ── Top Bar ── */}
      <div className="flex items-center justify-between py-3 border-b mb-4">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <div className="flex items-center gap-3">
          {form.Status && <StatusBadge status={form.Status} />}
          {isEditable && (
            <button onClick={handleSubmit} disabled={isSaving || qoV2.isSaving || isKirim}
              className="px-4 py-1.5 rounded border text-sm font-semibold text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-60">
              {isSaving || qoV2.isSaving ? "Menyimpan…" : "Simpan"}
            </button>
          )}
          {isEditable && bisaKirim && (
            <button onClick={handleKirimApproval} disabled={isSaving || qoV2.isSaving || isKirim}
              className="px-4 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-60"
              style={{ background: Colors.primary }}>
              {isKirim ? "Mengirim…" : "Kirim untuk Approval"}
            </button>
          )}
        </div>
      </div>

      {/* ── Peringatan edit QO yang sudah disetujui ── */}
      {isApprovedEdit && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-sm text-amber-800">
          Quotation ini <strong>sudah disetujui</strong>. Perubahan langsung berlaku tanpa approval ulang
          dan dipakai untuk JR berikutnya; JR & invoice yang sudah ada tidak berubah. Masa berlaku tidak
          boleh dipersempit hingga JR yang sudah ada berada di luar kontrak.
        </div>
      )}

      {/* ── Penanda induk (mode amandemen) ── */}
      {induk && (
        <div className="bg-purple-50 border border-purple-200 rounded p-4 mb-4">
          <div className="text-xs font-bold uppercase text-purple-700 tracking-wide mb-1">
            Amandemen atas kontrak
          </div>
          <div className="font-mono text-sm text-purple-900">{induk.QoNo}</div>
          <div className="text-sm text-purple-800 mt-0.5">
            {induk.NamaKlien}
            {induk.NoKontrak && <> &middot; {induk.NoKontrak}</>}
          </div>
          {induk.StartDate && induk.ExpireDate && (
            <div className="text-xs text-purple-700 mt-1">
              Masa berlaku induk: {induk.StartDate} &rarr; {induk.ExpireDate}
            </div>
          )}
          <p className="text-xs text-purple-700 mt-2">
            Aktivitas dan seluruh barisnya sudah <strong>disalin dari kontrak induk</strong>,
            jadi Anda tidak perlu mengisi ulang. Ubah yang berubah, dan{" "}
            <strong>hapus baris yang tidak termasuk dalam amandemen ini</strong> &mdash;
            baris yang dihapus tetap berlaku mengikuti kontrak induk.
          </p>
          <p className="text-xs text-purple-700 mt-1">
            Klien mengikuti induknya dan tidak bisa diubah di sini. Nomor kontrak
            terisi dari induk tapi <strong>boleh diubah</strong> bila dokumen
            amandemen ini punya nomor sendiri.
          </p>
          <button type="button" onClick={keluarModeAmandemen}
            className="mt-2 text-xs font-semibold text-purple-800 underline hover:text-purple-900">
            Bukan amandemen? Buat QO baru dengan form kosong
          </button>
        </div>
      )}

      {/* ── Header Form ── */}
      <div className="bg-white rounded border p-4 mb-4">
        <h3 className="text-sm font-bold uppercase text-gray-500 mb-4 tracking-wide">
          {induk ? "Informasi Amandemen" : "Informasi Kontrak"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* No Kontrak */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              No. Kontrak <span className="text-red-500">*</span>
            </label>
            <input type="text" value={form.NoKontrak ?? ""}
              onChange={e => updateHeader("NoKontrak", e.target.value)}
              onBlur={e => updateHeader("NoKontrak", e.target.value.trim())}
              placeholder="Contoh: 14.417/RKSHO-DIR/XI/2025"
              maxLength={100}
              className={`w-full border rounded px-3 py-2 text-sm outline-none ${
                errors.NoKontrak ? "border-red-500" : "border-gray-300"}`} />
            {/* Amandemen ikut bisa mengubah nomor kontrak: dokumen addendum bisa
                terbit dengan nomornya sendiri. Tautan ke induk TIDAK bergantung
                pada nomor ini — yang mengikat adalah FK "ParentQoID". */}
            {induk && (
              <p className="text-xs italic text-gray-500 mt-1">
                Terisi dari kontrak induk. Ubah bila dokumen amandemen ini bernomor sendiri.
              </p>
            )}
            {errors.NoKontrak && <p className="text-red-500 text-xs mt-1">{errors.NoKontrak}</p>}
          </div>

          {/* Nama Klien */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              Nama Klien <span className="text-red-500">*</span>
            </label>
            <Select menuPortalTarget={document.body}
              value={(klien.minData ?? []).filter(o => o.value === form.KlienID)}
              onChange={e => updateHeader("KlienID", e.value)}
              options={klien.minData ?? []}
              placeholder="Pilih klien…"
              isDisabled={!!induk || form.Status === "approved"}
              className={errors.KlienID ? "border-red-500 rounded" : ""} />
            {induk && <p className="text-xs italic text-gray-500 mt-1">Mengikuti kontrak induk.</p>}
            {!induk && form.Status === "approved" && (
              <p className="text-xs italic text-gray-500 mt-1">Customer QO yang sudah disetujui tidak bisa diganti.</p>
            )}
            {errors.KlienID && <p className="text-red-500 text-xs mt-1">{errors.KlienID}</p>}
          </div>

          {/* Tanggal Mulai */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              Tanggal Mulai Kontrak <span className="text-red-500">*</span>
            </label>
            <DatePicker dateFormat="dd/MM/yyyy"
              className={`w-full border rounded px-3 py-2 text-sm outline-none ${
                errors.StartDate ? "border-red-500" : "border-gray-300"}`}
              selected={form.StartDate ? new Date(form.StartDate) : null}
              onChange={date => updateHeader("StartDate", date?.toISOString().slice(0, 10))} />
            {errors.StartDate && <p className="text-red-500 text-xs mt-1">{errors.StartDate}</p>}
          </div>

          {/* Masa Berlaku */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              Masa Berlaku (Akhir Kontrak) <span className="text-red-500">*</span>
            </label>
            <DatePicker dateFormat="dd/MM/yyyy"
              className={`w-full border rounded px-3 py-2 text-sm outline-none ${
                errors.ExpireDate ? "border-red-500" : "border-gray-300"}`}
              selected={form.ExpireDate ? new Date(form.ExpireDate) : null}
              onChange={date => updateHeader("ExpireDate", date?.toISOString().slice(0, 10))} />
            {errors.ExpireDate && <p className="text-red-500 text-xs mt-1">{errors.ExpireDate}</p>}
          </div>

          {/* Deskripsi Aktifitas */}
          <div className="md:col-span-2">
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              Deskripsi Aktifitas <span className="text-red-500">*</span>
            </label>
            <input type="text" value={form.WorkDesc ?? ""}
              onChange={e => updateHeader("WorkDesc", e.target.value)}
              className={`w-full border rounded px-3 py-2 text-sm outline-none ${
                errors.WorkDesc ? "border-red-500" : "border-gray-300"}`} />
            {errors.WorkDesc && <p className="text-red-500 text-xs mt-1">{errors.WorkDesc}</p>}
          </div>
        </div>
      </div>

      {/* ── Parameter Handling Fee & FSC ── */}
      <div className="bg-white rounded border p-4 mb-4">
        <h3 className="text-xs font-bold uppercase text-gray-500 mb-1 tracking-wide">
          Parameter Margin
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          Persentase margin yang dapat dipungut untuk klien dan quotation ini, menurut rentang nilai (Fee Start – Fee End).
        </p>
        {errors.FeeParams && <p className="text-red-500 text-xs mb-2">{errors.FeeParams}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FeeParamBlock title="Parameter Handling Fee" rows={form.HandlingFeeParams}
            onChange={rows => setForm(prev => ({ ...prev, HandlingFeeParams: rows }))} />
          <FeeParamBlock title="Parameter FSC" rows={form.FscParams}
            onChange={rows => setForm(prev => ({ ...prev, FscParams: rows }))} />
        </div>

        {/* Activity yang dikenai Tagihan Fuel Surcharge */}
        <div className="mt-4 pt-3 border-t">
          <h4 className="text-xs font-bold uppercase text-gray-500 mb-1 tracking-wide">Tagihan Fuel Surcharge (FSC)</h4>
          <p className="text-xs text-gray-400 mb-2">
            Centang activity yang dikenai FSC. Di MJS, tagihan item Port Service yang namanya memuat activity ini
            dikalikan persentase Parameter FSC sesuai harga BBM Solar (Base Price) pada tanggal activity.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {FSC_ACTIVITIES.map(o => (
              <label key={o.value} className="flex items-center gap-2 cursor-pointer select-none text-sm">
                <input type="checkbox" className="w-4 h-4"
                  checked={(form.FscActivities ?? []).includes(o.value)}
                  onChange={() => setForm(prev => {
                    const cur = prev.FscActivities ?? [];
                    return { ...prev, FscActivities: cur.includes(o.value) ? cur.filter(x => x !== o.value) : [...cur, o.value] };
                  })} />
                <span className="font-medium">{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* ── Activity Selector ── */}
      <div className="bg-white rounded border p-4 mb-4">
        <h3 className="text-xs font-bold uppercase text-gray-500 mb-3 tracking-wide">
          Pilihan Aktivitas <span className="text-red-500">*</span>
        </h3>
        {errors.Activities && <p className="text-red-500 text-xs mb-2">{errors.Activities}</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {ACTIVITY_ORDER.map(name => (
            <label key={name} className="flex items-center gap-2 cursor-pointer select-none text-sm">
              <input type="checkbox"
                checked={isActivityActive(name)}
                onChange={() => toggleActivity(name)}
                className="w-4 h-4" />
              <span className="font-medium">{ACTIVITY_LABELS[name]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ── Activity Detail Sections ── */}
      <div className="space-y-4">

        {/* HANDLING FEE */}
        {isActivityActive("HANDLING_FEE") && (
          <ActivitySection title="Handling Fee">
            <AddButton onClick={() => addLine("HANDLING_FEE", "SERVICE", {
              ServiceType: "V", ItemRefType: "FREE" })} />
            <LineTable
              headers={["Item"]}
              feeMode
              rows={linesOf("HANDLING_FEE")}
              onDelete={i => deleteLine("HANDLING_FEE", "SERVICE", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("HANDLING_FEE", "SERVICE", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <div className="w-48">
                    <Select menuPortalTarget={document.body}
                      value={HANDLING_FEE_ITEMS.filter(o => o.value === row.ItemName)}
                      onChange={e => updateLine("HANDLING_FEE", "SERVICE", i, {
                        ItemName: e.value, ItemDisplay: e.label })}
                      options={HANDLING_FEE_ITEMS} />
                  </div>
                </TD>
              )} />
          </ActivitySection>
        )}

        {/* PORT SERVICES */}
        {isActivityActive("PORT_SERVICES") && (
          <ActivitySection title="Port Services">
            <AddButton onClick={() => addLine("PORT_SERVICES", "SERVICE", {
              ServiceType: "V", ItemRefType: "FREE" })} />
            <LineTable
              headers={["Layanan"]}
              rows={linesOf("PORT_SERVICES")}
              onDelete={i => deleteLine("PORT_SERVICES", "SERVICE", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("PORT_SERVICES", "SERVICE", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <div className="flex items-center gap-2 w-64">
                    <ServiceTypeBadge type={row.ServiceType} />
                    <div className="flex-1">
                      <Select menuPortalTarget={document.body}
                        value={portServiceValue(row)}
                        onChange={e => updateLine("PORT_SERVICES", "SERVICE", i, {
                          ItemName: e.value, ItemDisplay: e.label })}
                        options={portServiceOptions} />
                    </div>
                  </div>
                </TD>
              )} />
          </ActivitySection>
        )}

        {/* RENTAL */}
        {isActivityActive("RENTAL") && (
          <ActivitySection title="Rental">
            <AddButton onClick={() => addLine("RENTAL", "SERVICE", { ServiceType: "V", ItemRefType: "RENTAL" })} />
            <LineTable
              headers={["Kategori", "Unit"]}
              rows={linesOf("RENTAL")}
              onDelete={i => deleteLine("RENTAL", "SERVICE", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("RENTAL", "SERVICE", i, u)}
              renderCells={(row, i) => {
                const catLabel = { "1": "Heavy Equipment", "2": "Lifting/Rigging", "3": "CCU" }[row.CategoryID] ?? "-";
                return (
                  <>
                    <TD><span className="text-xs text-gray-500">{catLabel}</span></TD>
                    <TD>
                      <div className="w-48">
                        <Select menuPortalTarget={document.body}
                          value={rentalSelect.filter(o => o.value === row.ItemRefID)}
                          onChange={e => updateLine("RENTAL", "SERVICE", i, {
                            ItemRefID: e.value, ItemName: e.value,
                            ItemDisplay: e.label, CategoryID: e.CategoryID })}
                          options={rentalSelect} />
                      </div>
                    </TD>
                  </>
                );
              }} />
          </ActivitySection>
        )}

        {/* WASTE MANAGEMENT */}
        {isActivityActive("WASTE_MANAGEMENT_SERVICES") && (
          <ActivitySection title="Waste Management Services">
            <AddButton onClick={() => addLine("WASTE_MANAGEMENT_SERVICES", "SERVICE", {
              ServiceType: "V", ItemRefType: "WASTE" })} />
            <LineTable
              headers={["Jenis Limbah", "Kode HAZ"]}
              rows={linesOf("WASTE_MANAGEMENT_SERVICES")}
              onDelete={i => deleteLine("WASTE_MANAGEMENT_SERVICES", "SERVICE", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("WASTE_MANAGEMENT_SERVICES", "SERVICE", i, u)}
              renderCells={(row, i) => (
                <>
                  <TD>
                    <div className="w-40">
                      <Select menuPortalTarget={document.body}
                        value={(waste.minData ?? []).filter(o => o.value === row.ItemName)}
                        onChange={e => updateLine("WASTE_MANAGEMENT_SERVICES", "SERVICE", i, {
                          ItemName: e.value, ItemDisplay: e.label })}
                        options={waste.minData ?? []} />
                    </div>
                  </TD>
                  <TD>
                    <div className="w-32">
                      <Select menuPortalTarget={document.body}
                        value={(limbah.minData ?? []).filter(o => o.value === row.ItemRefID)}
                        onChange={e => updateLine("WASTE_MANAGEMENT_SERVICES", "SERVICE", i, {
                          ItemRefID: e.value, CategoryID: e.value })}
                        options={limbah.minData ?? []} />
                    </div>
                  </TD>
                </>
              )} />
          </ActivitySection>
        )}

        {/* AREA RENTAL */}
        {isActivityActive("AREA_RENTAL") && (
          <ActivitySection title="Area Rental" serviceType="F">
            <AddButton onClick={() => addLine("AREA_RENTAL", "SERVICE", {
              ServiceType: "F", ItemRefType: "AREA_RENTAL" })} />
            <LineTable
              headers={["Area"]}
              rows={linesOf("AREA_RENTAL")}
              onDelete={i => deleteLine("AREA_RENTAL", "SERVICE", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("AREA_RENTAL", "SERVICE", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <div className="w-56">
                    <Select menuPortalTarget={document.body}
                      value={(areaRental.minData ?? []).filter(o => o.value === row.ItemRefID)}
                      onChange={e => updateLine("AREA_RENTAL", "SERVICE", i, {
                        ItemRefID: e.value, ItemName: e.value,
                        ItemDisplay: e.label, CategoryID: e.CategoryID })}
                      options={areaRental.minData ?? []} />
                  </div>
                </TD>
              )} />
          </ActivitySection>
        )}

        {/* MANPOWER */}
        {isActivityActive("MANPOWER") && (
          <ActivitySection title="Manpower">
            <AddButton onClick={() => addLine("MANPOWER", "SERVICE", {
              ServiceType: "V", ItemRefType: "LABOUR" })} />
            <LineTable
              headers={["Jenis Tenaga"]}
              rows={linesOf("MANPOWER")}
              onDelete={i => deleteLine("MANPOWER", "SERVICE", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("MANPOWER", "SERVICE", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <div className="w-48">
                    <Select menuPortalTarget={document.body}
                      value={(labour.minData ?? []).filter(o => o.value === row.ItemRefID)}
                      onChange={e => updateLine("MANPOWER", "SERVICE", i, {
                        ItemRefID: e.value, ItemName: e.label, ItemDisplay: e.label })}
                      options={labour.minData ?? []} />
                  </div>
                </TD>
              )} />
          </ActivitySection>
        )}

        {/* GENERAL SERVICE */}
        {isActivityActive("GENERAL_SERVICE") && (
          <div className="bg-white rounded border">
            <SectionHeader title="General Service" />
            <div className="p-4 space-y-4">
              {GENERAL_SUBTYPES.map(({ key, label, serviceType }) => (
                <GeneralSubSection key={key} subType={key} label={label} serviceType={serviceType}
                  rows={linesOf("GENERAL_SERVICE").filter(l => l.SubType === key)}
                  onAdd={() => addLine("GENERAL_SERVICE", "SERVICE", key === "ROOM"
                    // Room tak lagi dipilih per tipe: Capacity Person menggantikannya. Baris dibuat
                    // sebagai "Room All" (kamar ditentukan petugas per tanggal di JR/Akomodasi).
                    ? { SubType: key, ServiceType: serviceType, ItemRefType: "ROOM", ItemRefID: "0",
                        CategoryID: "0", ItemName: "Room", ItemDisplay: "Room All", CapacityPerson: "" }
                    : { SubType: key, ServiceType: serviceType, ItemRefType: "FREE" })}
                  onDelete={i => {
                    const allLines = linesOf("GENERAL_SERVICE");
                    const target   = allLines.filter(l => l.SubType === key)[i];
                    const globalIdx = (form.Lines ?? []).findIndex(l =>
                      l.LineGroup === "SERVICE" && l.ActivityName === "GENERAL_SERVICE" &&
                      l.SubType === key && l === target);
                    if (globalIdx >= 0) {
                      setForm(prev => {
                        const lines = [...prev.Lines];
                        lines.splice(globalIdx, 1);
                        return { ...prev, Lines: lines };
                      });
                    }
                  }}
                  onChange={(i, u) => {
                    const allLines = linesOf("GENERAL_SERVICE");
                    const subRows  = allLines.filter(l => l.SubType === key);
                    const target   = subRows[i];
                    const globalIdx = (form.Lines ?? []).indexOf(target);
                    if (globalIdx >= 0) {
                      setForm(prev => {
                        const lines = [...prev.Lines];
                        lines[globalIdx] = { ...lines[globalIdx], ...u };
                        return { ...prev, Lines: lines };
                      });
                    }
                  }}
                  // pass master data per sub-type
                  // Harus SAMA dengan penyaring di ActivityDetailV2.jsx ("Room"):
                  // kamar yang dipilih di QO wajib bisa dipilih lagi saat entri
                  // aktivitas. Dulu di sini "Class Room" — nol baris MRoom punya
                  // jenis itu, jadi dropdown Accomodation/Room selalu kosong.
                  //
                  // value "0" = opsi "All" yang disisipkan roomSlice, BUKAN baris
                  // MRoom — ia tak punya `jenis` sehingga selalu tersapu penyaring.
                  // Wajib diloloskan: "Room All" berarti kamar belum ditentukan dan
                  // dipilih per tanggal saat entri (lihat isRoomAll di
                  // GeneralServiceEntryEditor.jsx).
                  roomOptions={(room.minData ?? []).filter(o => o.jenis === "Room" || o.value === "0")}
                  clothingOptions={clothing.minData ?? []}
                  mealOptions={mealOptions}
                  generalOptions={generalUnit.minData ?? []}
                  fuelOptions={fuel.minData ?? []}
                  gsItemOptions={gsItem.minBySubType}
                  uomOptions={uomAll}
              uomLabelById={uomLabelById} />
              ))}
            </div>
          </div>
        )}

        {/* SUPPORTING: Equipment */}
        {(isActivityActive("PORT_SERVICES") || isActivityActive("WASTE_MANAGEMENT_SERVICES") || isActivityActive("RENTAL")) && (
          <ActivitySection title="Equipment">
            <AddButton onClick={() => addLine("", "EQUIPMENT", { ServiceType: "V", ItemRefType: "EQUIPMENT" })} />
            <LineTable
              headers={["Unit / Alat"]}
              rows={linesOf("", "EQUIPMENT")}
              onDelete={i => deleteLine("", "EQUIPMENT", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("", "EQUIPMENT", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <div className="w-56">
                    <Select menuPortalTarget={document.body}
                      value={(kendaraan.minData ?? []).filter(o => o.value === row.ItemRefID)}
                      onChange={e => updateLine("", "EQUIPMENT", i, {
                        ItemRefID: e.value, ItemName: e.label, ItemDisplay: e.label })}
                      options={kendaraan.minData ?? []} />
                  </div>
                </TD>
              )} />
          </ActivitySection>
        )}

        {/* SUPPORTING: Labour */}
        {(isActivityActive("PORT_SERVICES") || isActivityActive("WASTE_MANAGEMENT_SERVICES") || isActivityActive("RENTAL")) && (
          <ActivitySection title="Labour">
            <AddButton onClick={() => addLine("", "LABOUR", { ServiceType: "V", ItemRefType: "LABOUR" })} />
            <LineTable
              headers={["Jenis Tenaga"]}
              rows={linesOf("", "LABOUR")}
              onDelete={i => deleteLine("", "LABOUR", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("", "LABOUR", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <div className="w-48">
                    <Select menuPortalTarget={document.body}
                      value={(labour.minData ?? []).filter(o => o.value === row.ItemRefID)}
                      onChange={e => updateLine("", "LABOUR", i, {
                        ItemRefID: e.value, ItemName: e.label, ItemDisplay: e.label })}
                      options={labour.minData ?? []} />
                  </div>
                </TD>
              )} />
          </ActivitySection>
        )}

        {/* SUPPORTING: TWS */}
        {isActivityActive("WASTE_MANAGEMENT_SERVICES") && (
          <ActivitySection title="Temporary Waste Storage">
            <AddButton onClick={() => addLine("", "TWS", { ServiceType: "V", ItemRefType: "FREE" })} />
            <LineTable
              headers={["Lokasi Penyimpanan"]}
              rows={linesOf("", "TWS")}
              onDelete={i => deleteLine("", "TWS", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("", "TWS", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <input type="text" placeholder="Nama/kode lokasi"
                    className="w-48 border rounded px-2 py-1 text-sm"
                    value={row.ItemName ?? ""}
                    onChange={e => updateLine("", "TWS", i, { ItemName: e.target.value })} />
                </TD>
              )} />
          </ActivitySection>
        )}

        {/* SUPPORTING: Truck */}
        {isActivityActive("PORT_SERVICES") && (
          <ActivitySection title="Truck Entry">
            <AddButton onClick={() => addLine("", "TRUCK", { ServiceType: "V", ItemRefType: "FREE" })} />
            <LineTable
              headers={["Deskripsi"]}
              rows={linesOf("", "TRUCK")}
              onDelete={i => deleteLine("", "TRUCK", i)}
              uomOptions={uomAll}
              uomLabelById={uomLabelById}
              satuanKey="SatuanID"
              onChange={(i, u) => updateLine("", "TRUCK", i, u)}
              renderCells={(row, i) => (
                <TD>
                  <input type="text" placeholder="Deskripsi truck"
                    className="w-48 border rounded px-2 py-1 text-sm"
                    value={row.ItemName ?? ""}
                    onChange={e => updateLine("", "TRUCK", i, { ItemName: e.target.value })} />
                </TD>
              )} />
          </ActivitySection>
        )}

      </div>
    </div>
  );
}

// ── Helper sub-components ─────────────────────────────────────────

function ActivitySection({ title, serviceType, children }) {
  return (
    <div className="bg-white rounded border">
      <SectionHeader title={title} />
      {serviceType && (
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b">
          <ServiceTypeBadge type={serviceType} />
          {serviceType === "F" && (
            <span className="text-xs text-blue-600 italic">
              (otomatis masuk JR setiap hari)
            </span>
          )}
        </div>
      )}
      <div className="p-3">{children}</div>
    </div>
  );
}

function GeneralSubSection({
  subType, label, serviceType, rows, onAdd, onDelete, onChange,
  roomOptions, clothingOptions, mealOptions, generalOptions, fuelOptions, gsItemOptions, uomOptions, uomLabelById,
}) {
  return (
    <div className="border rounded">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        <ServiceTypeBadge type={serviceType} />
        {serviceType === "F" && (
          <span className="text-xs text-blue-600 italic">
            (otomatis masuk JR setiap hari)
          </span>
        )}
      </div>
      <div className="p-3">
        <AddButton onClick={onAdd} />
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-sm">
              <thead>
                <tr>
                  <TH className="w-8" />
                  <TH>{getSubTypeHeader(subType)}</TH>
                  <TH>UoM</TH>
                  <TH>Min Usage</TH>
                  <TH>Rate (Rp)</TH>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <TD><DeleteBtn onClick={() => onDelete(i)} /></TD>
                    <TD>
                      <div className="w-48">
                        {renderGeneralCell(subType, row, i, onChange,
                          roomOptions, clothingOptions, mealOptions, generalOptions, fuelOptions, gsItemOptions)}
                      </div>
                    </TD>
                    <TD>
                      <div className="w-32">
                        <Select menuPortalTarget={document.body}
                          value={nilaiUom(uomOptions, uomLabelById, row.SatuanID)}
                          onChange={e => onChange(i, { SatuanID: e.value })}
                          options={uomOptions} />
                      </div>
                    </TD>
                    <TD>
                      <QtyInput className="w-28 border rounded px-2 py-1 text-right"
                        value={row.MinUsage ?? 0}
                        onChange={v => onChange(i, { MinUsage: parseFloat(v) || 0 })} />
                    </TD>
                    <TD>
                      <NumberFormat thousandSeparator className="w-36 border rounded px-2 py-1"
                        value={row.Rate ?? 0}
                        onValueChange={({ value }) => onChange(i, { Rate: parseFloat(value) || 0 })} />
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function getSubTypeHeader(subType) {
  return { ROOM: "Capacity Person", LAUNDRY: "Jenis Pakaian", MEALS: "Jenis Makanan",
    WOODEN: "Aktivitas", DRILL: "Jenis Air", OTHERS: "Aktivitas", VEHICLE: "Unit" }[subType] ?? "Item";
}

// Nilai tersimpan yang tak ada di daftar aktif tetap tampil, dirakit dari
// ItemDisplay baris itu — pola yang sama dengan portServiceValue. Tanpa ini item
// yang sudah dinonaktifkan di master akan tampil kosong lalu hilang saat QO
// disimpan ulang.
function masterItemValue(options, row) {
  if (!row?.ItemName) return null;
  return options.find(o => o.value === row.ItemName)
    ?? { value: row.ItemName, label: row.ItemDisplay || row.ItemName };
}

function renderGeneralCell(subType, row, i, onChange,
  roomOptions, clothingOptions, mealOptions, generalOptions, fuelOptions, gsItemOptions = {}) {
  switch (subType) {
    case "ROOM":
      // Capacity Person = maks. penghuni klien dalam satu room. Penanda "Room All" (ItemRefID "0")
      // dipertahankan: editor entri JR mencocokkannya dengan /room\s*all/i untuk memunculkan
      // pemilih kamar per tanggal. Baris warisan yang sudah menunjuk tipe room tertentu
      // dibiarkan (hanya kapasitasnya yang diisi/diubah).
      return (
        <div className="flex items-center gap-2">
          <input type="number" min={1} max={50} step={1}
            className="w-24 border rounded px-2 py-1 text-right"
            placeholder="0"
            value={row.CapacityPerson || ""}
            onChange={e => {
              const n = e.target.value === "" ? "" : Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1));
              onChange(i, row.ItemRefID
                ? { CapacityPerson: n }
                : { CapacityPerson: n, ItemRefID: "0", CategoryID: "0", ItemName: "Room",
                    ItemDisplay: "Room All", ItemRefType: "ROOM" });
            }} />
          <span className="text-xs text-gray-500">orang / room</span>
        </div>
      );
    case "LAUNDRY":
      return (
        <Select menuPortalTarget={document.body}
          value={clothingOptions.filter(o => o.value === row.ItemRefID)}
          onChange={e => onChange(i, { ItemRefID: e.value, ItemName: e.label, ItemDisplay: e.label, ItemRefType: "ROOM" })}
          options={clothingOptions} />
      );
    case "MEALS":
      return (
        <Select menuPortalTarget={document.body}
          value={mealOptions.filter(o => o.value === row.ItemName)}
          onChange={e => onChange(i, { ItemName: e.value, ItemDisplay: e.label })}
          options={mealOptions} />
      );
    // Wooden / Drill / Others: daftar item dari master MGeneralServiceItem
    // (menu Master > Item General Service).
    case "WOODEN":
    case "DRILL":
    case "OTHERS": {
      const opts = gsItemOptions[subType] ?? [];
      return (
        <Select menuPortalTarget={document.body}
          value={masterItemValue(opts, row)}
          onChange={e => onChange(i, { ItemName: e.value, ItemDisplay: e.label })}
          options={opts} />
      );
    }
    case "VEHICLE":
      return (
        <Select menuPortalTarget={document.body}
          value={generalOptions.filter(o => o.value === row.ItemRefID)}
          onChange={e => onChange(i, {
            ItemRefID: e.value, ItemName: e.label,
            ItemDisplay: e.label, ItemRefType: "GENERAL_UNIT" })}
          options={generalOptions} />
      );
    default:
      return (
        <input type="text" className="w-full border rounded px-2 py-1 text-sm"
          value={row.ItemName ?? ""}
          onChange={e => onChange(i, { ItemName: e.target.value })} />
      );
  }
}
